import { Component, inject, input, signal } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { catchError, delay, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { G4Service, GetG4CustomerResponse } from '../../services/g4.service';
import { FeedbackFailComponent, FeedbackSuccessComponent, FormFieldLoadingComponent } from 'ok-angular-shared/components';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'adm-g4-customer-number',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FeedbackSuccessComponent,
    FeedbackFailComponent,
    FormFieldLoadingComponent,
  ],
  templateUrl: './adm-g4-customer-number.component.html',
  styleUrl: './adm-g4-customer-number.component.scss',
})
export class AdmG4CustomerNumberComponent {
  g4Service = inject(G4Service);
  customerDetails = signal<GetG4CustomerResponse | null>(null);
  control = input.required<FormControl<string | null>>();

  private patternRegex = /^\d{5,7}$/;

  constructor() {
    toObservable(this.control)
      .pipe(
        distinctUntilChanged(),
        tap(ctrl => {
          ctrl.setValidators([Validators.required, Validators.pattern(this.patternRegex)]);
          ctrl.setAsyncValidators([this.g4CustomerExistsValidator()]);
          ctrl.updateValueAndValidity();
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private g4CustomerExistsValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }
      return of(control.value).pipe(
        delay(300),
        switchMap(value => this.g4Service.getG4Customer(value)),
        map(result => {
          if (result) {
            this.customerDetails.set(result);
            return null;
          }
          this.customerDetails.set(null);
          return { customerDoesNotExist: true };
        }),
        catchError(() => {
          this.customerDetails.set(null);
          return of({ customerDoesNotExist: true });
        })
      );
    };
  }
}
